import {
  Home,
  Sparkles,
  Zap,
  BookOpen,
  ShoppingBag,
  ClipboardList,
  MessageSquare,
  Heart,
  Users,
  User,
  HelpCircle,
  Calendar,
  GraduationCap,
  Award,
  FileText,
  Inbox,
  Briefcase,
  Send,
  Plus,
  TrendingUp,
  DollarSign,
  WalletCards,
  ShieldCheck,
  Receipt,
  Settings,
  BarChart3,
  Ticket,
  AlertTriangle,
  Activity,
  UserCheck,
  Layers,
  Star,
  Flame,
  Radio,
  Smartphone,
  Hash,
} from "lucide-react";

/* KD9 — Menus per role (Sistema Maestro de Identidad · Fase 4).
   Inheritance rule: every role INHERITS the base USER sections and adds
   its own role-specific sections on top. The Cuenta section always renders
   at the bottom of the sidebar. */

/* ----- USER BASE (inherited by every role) ----- */
const USER_PRIORITY_SECTION = {
  sectionKey: "sidebar.section.acompanamiento",
  priority: true,
  items: [
    { to: "/app/videollamadas-kd9", label: "Videollamadas KD9", icon: Calendar, permission: "accompaniment.request" },
    { to: "/app/acompanamiento", labelKey: "menu.acompanamiento", icon: Heart, permission: "accompaniment.request" },
    { to: "/app/hablar-consultor", label: "Hablar con consultor", icon: MessageSquare, permission: "accompaniment.request" },
    { to: "/app/consulta-rapida", label: "Consulta rápida", icon: Zap, permission: "accompaniment.request" },
  ],
};

const USER_PROCESS_SECTION = {
  sectionKey: "sidebar.section.proceso",
  items: [
    { to: "/app/inicio", labelKey: "menu.dashboard", icon: Home },
    { to: "/app/diagnosticos", labelKey: "menu.solicitarDiag", icon: Sparkles, permission: "diagnostics.buy" },
    {
      to: "/app/mis-diagnosticos",
      labelKey: "menu.misDiag",
      icon: ClipboardList,
      permission: "diagnostics.buy",
    },
    { to: "/app/academia", labelKey: "menu.academia", icon: BookOpen, permissionsAny: ["courses.buy", "academy.view"] },
    { to: "/app/tienda", labelKey: "menu.tienda", icon: ShoppingBag, permission: "marketplace.buy" },
    {
      to: "/app/mis-compras-marketplace",
      label: "Mis compras Marketplace",
      icon: ClipboardList,
      permission: "marketplace.buy",
    },
    { to: "/app/energia-kd", label: "Energía KD9 · OnePay", icon: Flame },
    { to: "/app/pagos-terceros", label: "Pago a terceros", icon: Send, permission: "onepay.third_party.use" },
    { to: "/app/templo-vivo", label: "Templo Vivo KD9", icon: Radio },
    { to: "/app/oraculo", label: "Oráculo", icon: Sparkles },
    { to: "/app/numerologia", label: "Numerología Viva KD9", icon: Hash },
    { to: "/app/horoscopo", label: "Horóscopo diario", icon: Star },
    { to: "/app/agenda", labelKey: "menu.agenda", icon: Calendar, permissionsAny: ["agenda.view", "agenda.manage"] },
    {
      to: "/app/mis-solicitudes",
      labelKey: "menu.misSolicitudes",
      icon: UserCheck,
    },
  ],
};

const MARKETPLACE_BUYER_SECTION = {
  sectionKey: "Marketplace KD9",
  items: [
    { to: "/app/tienda", labelKey: "menu.tienda", icon: ShoppingBag, permission: "marketplace.buy" },
    {
      to: "/app/mis-compras-marketplace",
      label: "Mis compras Marketplace",
      icon: ClipboardList,
      permission: "marketplace.buy",
    },
  ],
};

const USER_ACCOUNT_SECTION = {
  sectionKey: "sidebar.section.cuenta",
  items: [
    { to: "/app/perfil", labelKey: "menu.perfil", icon: User, permission: "profile.manage" },
    { to: "/app/soporte", labelKey: "menu.soporte", icon: HelpCircle, permissionsAny: ["support.create_ticket", "tickets.view"] },
    { to: "/descarga", label: "Descargar KD9 para Android", icon: Smartphone },
    { to: "/descarga#iphone", label: "Descargar KD9 para iPhone", icon: Smartphone },
  ],
};

// Recargas propias: todos los roles salvo Dirección, sin heredar facultades operativas.
const ENERGY_PURCHASE_SECTION = {
  sectionKey: "Billetera KD9",
  items: [{ to: "/app/energia-kd", label: "Energía KD9 · OnePay", icon: Flame }],
};

// Explicit customer capabilities on Creator — never professional authority.
const CREATOR_PERSONAL_SERVICES = {
  sectionKey: "Mis servicios KD9",
  items: [
    { to: "/app/diagnosticos", label: "Solicitar diagnóstico", icon: Sparkles, permission: "diagnostics.buy" },
    { to: "/app/mis-diagnosticos", label: "Mis diagnósticos", icon: ClipboardList, permission: "diagnostics.buy" },
    { to: "/app/oraculo", label: "Oráculo KD9", icon: Sparkles },
    { to: "/app/academia", label: "Academia KD9", icon: BookOpen, permission: "academy.view" },
    { to: "/app/templo-vivo", label: "Templo Vivo KD9", icon: Radio },
    { to: "/app/agenda", label: "Mi agenda", icon: Calendar, permission: "agenda.view" },
  ],
};

const USER_BASE = [
  USER_PRIORITY_SECTION,
  USER_PROCESS_SECTION,
  USER_ACCOUNT_SECTION,
];

/* ----- Role-specific "extras" (rendered ABOVE the user base) ----- */
const STUDENT_EXTRAS = {
  sectionKey: "sidebar.section.academia",
  items: [
    {
      to: "/app/mis-cursos",
      label: "Mis cursos",
      icon: GraduationCap,
      permission: "academy.view",
    },
    { to: "/app/progreso", labelKey: "menu.progreso", icon: TrendingUp, permission: "academy.view" },
    { to: "/app/examenes", labelKey: "menu.examenes", icon: FileText, permission: "exams.take" },
    { to: "/app/materiales", labelKey: "menu.materiales", icon: Layers, permission: "academy.view" },
    {
      to: "/app/certificados",
      label: "Certificados",
      icon: Award,
      permission: "academy.view",
    },
  ],
};

const CONSULTOR_EXTRAS = {
  sectionKey: "sidebar.section.operacion",
  items: [
    { to: "/app/horoscopo", label: "Horóscopo diario", icon: Star },
    { to: "/app/numerologia", label: "Numerología Viva KD9", icon: Hash },
    {
      to: "/app/templo-vivo",
      label: "Templo Vivo KD9",
      icon: Radio,
    },
    {
      to: "/app/bolsa-diagnosticos",
      label: "Bolsa de diagnósticos",
      icon: Inbox,
      permission: "consultant.diagnostics.provide",
    },
    {
      to: "/app/bolsa-consultas",
      label: "Acompañamientos y llamadas",
      icon: Heart,
      permissionsAny: ["consultant.accompaniment.provide", "consultant.video_calls.provide"],
    },
    {
      to: "/app/consultas-directas",
      label: "Consultas directas",
      icon: MessageSquare,
      permissionsAny: ["consultant.quick_consultation.provide", "consultant.talk.provide"],
    },
    {
      to: "/app/oraculo-consultor",
      label: "Oráculo · Interpretar",
      icon: Sparkles,
      permission: "consultant.oracle.provide",
    },
    {
      to: "/app/academia",
      label: "Academia KD9",
      icon: BookOpen,
      permission: "academy.view",
    },
    {
      to: "/app/mis-trabajos",
      label: "Diagnósticos asignados",
      icon: Briefcase,
      permission: "consultant.diagnostics.provide",
    },
    {
      to: "/app/informes",
      label: "Enviados a revisión",
      icon: Send,
      permission: "consultant.diagnostics.provide",
    },
    { to: "/app/mis-ganancias", label: "Mis Ganancias", icon: WalletCards, permission: "diagnostics.work" },
    { to: "/app/finanzas", label: "Mis finanzas", icon: DollarSign, permissionsAny: ["diagnostics.work", "marketplace.earnings.view_own"] },
  ],
};

const CREADOR_EXTRAS = {
  sectionKey: "Mi tienda KD9",
  items: [
    { to: "/app/horoscopo", label: "Horóscopo diario", icon: Star },
    { to: "/app/numerologia", label: "Numerología Viva KD9", icon: Hash },
    {
      to: "/app/inicio",
      label: "Centro del creador",
      icon: Home,
      permission: "marketplace.products.view_own",
    },
    {
      to: "/app/mis-productos",
      label: "Productos y servicios",
      icon: ShoppingBag,
      permission: "marketplace.products.view_own",
    },
    {
      to: "/app/subir-producto",
      label: "Nueva publicación",
      icon: Plus,
      permission: "marketplace.products.create_draft",
    },
    {
      to: "/app/inventario",
      label: "Inventario y visibilidad",
      icon: Layers,
      permission: "marketplace.inventory.manage_own",
    },
    {
      to: "/app/pedidos-marketplace",
      label: "Pedidos por atender",
      icon: ClipboardList,
      permission: "marketplace.orders.view_own",
    },
    {
      to: "/app/finanzas",
      label: "Ventas, saldo y retiros",
      icon: DollarSign,
      permission: "marketplace.earnings.view_own",
    },
    {
      to: "/app/configuracion-tienda",
      label: "Configuración de tienda",
      icon: Settings,
      permission: "marketplace.store.manage_own",
    },
    {
      to: "/app/contenidos-academia",
      label: "Contenidos Academia",
      icon: BookOpen,
      permission: "courses.view_own_drafts",
    },
  ],
};

const SUPPORT_OVERVIEW = {
  sectionKey: "Soporte KD9",
  items: [
    { to: "/app/inicio", label: "Centro de soporte", icon: Home },
    { to: "/app/tienda", labelKey: "menu.tienda", icon: ShoppingBag, permission: "marketplace.buy" },
  ],
};

const SUPPORT_CASES = {
  sectionKey: "Comunicación y casos",
  items: [
    {
      to: "/app/whatsapp-notificaciones",
      label: "Centro WhatsApp KD9",
      icon: MessageSquare,
      flagRequired: "whatsapp_inbox_access",
    },
    {
      to: "/app/tickets",
      label: "Bandeja de soporte",
      icon: MessageSquare,
      permission: "tickets.view",
    },
    {
      to: "/app/tickets-escalados",
      label: "Casos escalados",
      icon: AlertTriangle,
      permission: "tickets.escalate",
    },
    {
      to: "/app/tickets-cerrados",
      label: "Historial resuelto",
      icon: Award,
      permission: "tickets.view",
    },
    {
      to: "/app/usuarios-problemas",
      label: "Usuarios con casos",
      icon: Users,
      permission: "users.view",
    },
    {
      to: "/app/diagnostico-sesiones",
      label: "Diagnóstico de sesiones",
      icon: Activity,
      permission: "activity.support",
    },
    {
      to: "/app/reportes-tecnicos",
      label: "Incidencias técnicas",
      icon: BarChart3,
      permission: "tickets.view",
    },
  ],
};

const SUPPORT_CROSS_PLATFORM = {
  sectionKey: "Resolución transversal",
  items: [
    {
      to: "/app/marketplace-admin",
      label: "Casos Marketplace",
      icon: ShoppingBag,
      permission: "marketplace.products.review",
    },
    {
      to: "/app/configuracion-operativa",
      label: "Parámetros vigentes",
      icon: Settings,
      permission: "system.config.view",
    },
  ],
};

const SUPPORT_ACCOUNT = {
  sectionKey: "Cuenta de Soporte",
  items: [
    { to: "/app/perfil", labelKey: "menu.perfil", icon: User, permission: "profile.manage" },
    {
      to: "/app/tickets",
      label: "Comunicación con soporte",
      icon: HelpCircle,
      permission: "tickets.view",
    },
  ],
};

const ADMIN_OVERVIEW = {
  sectionKey: "Administración KD9",
  items: [{ to: "/app/inicio", label: "Centro administrativo", icon: Home }],
};

const ADMIN_OPERATION = {
  sectionKey: "Operación diaria",
  items: [
    {
      to: "/app/asignaciones",
      labelKey: "menu.asignaciones",
      icon: UserCheck,
      permission: "assignments.manage",
    },
    {
      to: "/app/revision-diagnosticos",
      labelKey: "menu.revisionDiag",
      icon: ShieldCheck,
      permission: "diagnostics.review",
    },
    {
      to: "/app/acompanamientos-admin",
      labelKey: "menu.acompanamientosAdmin",
      icon: Heart,
      permission: "assignments.manage",
    },
    {
      to: "/app/agenda",
      label: "Agenda global",
      icon: Calendar,
      permission: "agenda.manage",
    },
  ],
};

const ADMIN_CONTROL = {
  sectionKey: "Control administrativo",
  items: [
    {
      to: "/app/whatsapp-notificaciones",
      label: "Centro WhatsApp KD9",
      icon: MessageSquare,
      flagRequired: "whatsapp_inbox_access",
    },
    {
      to: "/app/solicitudes-roles",
      labelKey: "menu.solicitudesRoles",
      icon: UserCheck,
      permission: "consultant.requests.review",
    },
    {
      to: "/app/usuarios",
      label: "Consulta de usuarios",
      icon: Users,
      permission: "users.view",
    },
    {
      to: "/app/actividad-usuarios",
      label: "Actividad de usuarios",
      icon: Activity,
      permission: "activity.overview",
    },
    {
      to: "/app/radar-retencion",
      label: "Radar de retención",
      icon: TrendingUp,
      permission: "activity.overview",
    },
    {
      to: "/app/marketplace-admin",
      label: "Revisión Marketplace",
      icon: ShoppingBag,
      permission: "marketplace.products.review",
    },
    {
      to: "/app/revision-contenidos-creador",
      label: "Contenidos de creadores",
      icon: BookOpen,
      permission: "courses.review",
    },
    {
      to: "/app/configuracion-pagos",
      label: "Métodos y cuentas de pago",
      icon: Receipt,
      permission: "payments.settings.manage",
    },
    {
      to: "/app/tickets",
      labelKey: "menu.ticketsSoporte",
      icon: Ticket,
      permission: "tickets.view",
    },
    {
      to: "/app/reportes",
      label: "Reportes operativos",
      icon: BarChart3,
      permission: "system.config.view",
    },
    {
      to: "/app/configuracion-operativa",
      label: "Parámetros vigentes",
      icon: Settings,
      permission: "system.config.view",
    },
  ],
};

const ADMIN_ACCOUNT = {
  sectionKey: "Cuenta administrativa",
  items: [
    { to: "/app/perfil", labelKey: "menu.perfil", icon: User, permission: "profile.manage" },
    { to: "/app/soporte", labelKey: "menu.soporte", icon: HelpCircle, permissionsAny: ["support.create_ticket", "tickets.view"] },
  ],
};

const DIRECTOR_OVERVIEW = {
  sectionKey: "Dirección KD9",
  items: [{ to: "/app/inicio", label: "Resumen ejecutivo", icon: Home }],
};

const DIRECTOR_DECISIONS = {
  sectionKey: "Centro de decisiones",
  items: [
    { to: "/app/energia-kd-director", label: "Gobierno Energía KD", icon: Flame, permission: "system.config" },
    { to: "/app/gobierno-pagos-terceros", label: "Gobierno Pago a terceros", icon: ShieldCheck, permission: "onepay.third_party.manage" },
    { to: "/app/costos-kd9", label: "Centro de Costos KD9", icon: DollarSign, permission: "system.config" },
    { to: "/app/templo-vivo-director", label: "Control Templo Vivo", icon: Radio, permission: "system.config" },
    {
      to: "/app/acompanamientos-director",
      label: "Acompañamientos, llamadas y video",
      icon: Heart,
      permission: "system.config",
    },
    { to: "/app/configuracion-consulta-rapida", label: "Servicios de Consultor", icon: Settings, permission: "system.config" },
    {
      to: "/app/configuracion-pagos",
      label: "Bancos, QR y avisos",
      icon: Receipt,
      permission: "payments.settings.manage",
    },
    { to: "/app/configuracion-ganancias-consultor", label: "Configurar ganancias de Consultores", icon: WalletCards, permission: "system.config" },
    {
      to: "/app/gestion-retiros",
      label: "Gestión de retiros",
      icon: DollarSign,
      permission: "system.config",
    },
    {
      to: "/app/premium",
      label: "Academia Premium",
      icon: GraduationCap,
      permission: "premium.activate",
    },
    {
      to: "/app/solicitudes-roles",
      label: "Solicitudes de roles",
      icon: UserCheck,
      permission: "consultant.requests.review",
    },
  ],
};

const DIRECTOR_ORACLE = {
  sectionKey: "Oráculo KD9",
  priority: true,
  items: [
    {
      to: "/app/numerologia-director",
      label: "Gobierno Numerología Viva",
      icon: Hash,
      permission: "system.config",
    },
    {
      to: "/app/oraculo-director",
      label: "Gobierno del Oráculo",
      icon: Sparkles,
      permission: "system.config",
    },
  ],
};

const DIRECTOR_DIAGNOSTICS = {
  sectionKey: "Diagnósticos KD9",
  items: [
    {
      to: "/app/super-diagnosticos",
      label: "Catálogo y formularios",
      icon: Sparkles,
      permission: "diagnostics.create",
    },
    {
      to: "/app/revision-diagnosticos",
      label: "Bolsa y asignación",
      icon: Inbox,
      permission: "diagnostics.review",
    },
    {
      to: "/app/entregas-finales",
      label: "Entregas finales",
      icon: ShieldCheck,
      permission: "diagnostics.delivery.approve",
    },
  ],
};

const DIRECTOR_ACADEMY = {
  sectionKey: "Academia KD9",
  items: [
    {
      to: "/app/super-cursos",
      label: "Cursos, módulos y evaluaciones",
      icon: GraduationCap,
      permission: "courses.create",
    },
    {
      to: "/app/revision-contenidos-creador",
      label: "Revisión de creadores",
      icon: BookOpen,
      permission: "courses.review",
    },
  ],
};


const DIRECTOR_MARKETPLACE = {
  sectionKey: "Marketplace KD9",
  priority: true,
  items: [
    {
      to: "/app/super-marketplace",
      label: "Centro de Gobierno Marketplace",
      icon: ShoppingBag,
      permission: "marketplace.manage",
    },
  ],
};

const DIRECTOR_LANDING = {
  sectionKey: "Landing y publicaciones",
  items: [
    {
      to: "/app/landing-vivo",
      label: "Horóscopo Diario KD9",
      icon: Star,
      permission: "system.config",
    },
    {
      to: "/app/control-landing",
      label: "Control del landing",
      icon: Layers,
      permission: "system.config",
    },
  ],
};

const DIRECTOR_GOVERNANCE = {
  sectionKey: "Gobierno del ecosistema",
  items: [
    {
      to: "/app/usuarios",
      label: "Usuarios",
      icon: Users,
      permission: "users.view",
    },
    {
      to: "/app/actividad-usuarios",
      label: "Actividad y sesiones",
      icon: Activity,
      permission: "activity.overview",
    },
    {
      to: "/app/radar-retencion",
      label: "Radar de Retención KD9",
      icon: TrendingUp,
      permission: "activity.overview",
    },
    {
      to: "/app/super-roles",
      label: "Roles y equipo de confianza",
      icon: ShieldCheck,
      permission: "roles.manage",
    },
    {
      to: "/app/creadores",
      label: "Gobierno de Creadores",
      icon: Users,
      permission: "users.manage",
    },
    {
      to: "/app/configuracion-global",
      label: "Configuración global",
      icon: Settings,
      permission: "system.config",
    },
  ],
};

const DIRECTOR_CONTROL = {
  sectionKey: "Control y trazabilidad",
  items: [
    {
      to: "/app/analitica-global",
      label: "Analítica global",
      icon: TrendingUp,
      permission: "audit.view",
    },
    {
      to: "/app/auditoria",
      label: "Auditoría",
      icon: BarChart3,
      permission: "audit.view",
    },
    {
      to: "/app/whatsapp-notificaciones",
      label: "Centro WhatsApp KD9",
      icon: MessageSquare,
    },
    {
      to: "/app/tickets-escalados",
      label: "Incidentes escalados",
      icon: AlertTriangle,
      permission: "support.manage",
    },
  ],
};

const DIRECTOR_ACCOUNT = {
  sectionKey: "Cuenta de Dirección",
  items: [
    { to: "/app/perfil", labelKey: "menu.perfil", icon: User, permission: "profile.manage" },
    { to: "/app/soporte", labelKey: "menu.soporte", icon: HelpCircle, permissionsAny: ["support.create_ticket", "tickets.view"] },
  ],
};

/* ----- Gobierno global de módulos (Día 3) ----- */
const MODULE_ROUTE_RULES = [
  ["diagnostics", [
    "/app/diagnosticos", "/app/mis-diagnosticos", "/app/estudios",
    "/app/bolsa-diagnosticos", "/app/mis-trabajos", "/app/informes",
    "/app/revision-diagnosticos", "/app/entregas-finales", "/app/super-diagnosticos",
  ]],
  ["academy", [
    "/app/academia", "/app/mis-cursos", "/app/progreso", "/app/examenes",
    "/app/materiales", "/app/certificados", "/app/super-cursos",
    "/app/revision-contenidos-creador", "/app/contenidos-academia",
  ]],
  ["marketplace", [
    "/app/tienda", "/app/mis-compras-marketplace", "/app/mis-productos",
    "/app/subir-producto", "/app/inventario", "/app/pedidos-marketplace",
    "/app/ventas", "/app/ganancias", "/app/configuracion-tienda",
    "/app/marketplace-admin", "/app/super-marketplace",
  ]],
  ["accompaniment", [
    "/app/acompanamiento", "/app/videollamadas-kd9", "/app/bolsa-consultas",
    "/app/acompanamientos-admin", "/app/acompanamientos-director",
  ]],
  ["consultant_services", [
    "/app/hablar-consultor", "/app/consulta-rapida", "/app/consultas-directas",
    "/app/configuracion-consulta-rapida",
  ]],
  ["temple_live", ["/app/templo-vivo", "/app/templo-vivo-director"]],
  ["oracle", ["/app/oraculo", "/app/numerologia", "/app/numerologia-director", "/app/oraculo-consultor", "/app/oraculo-director"]],
];

export function moduleForPath(path = "") {
  for (const [moduleKey, routes] of MODULE_ROUTE_RULES) {
    if (routes.some((route) => path === route || path.startsWith(`${route}/`))) return moduleKey;
  }
  return null;
}

/* ----- Helper: build final sections list — extras first, user base second, account always last ----- */
export function isMenuItemAllowed(item, permissions) {
  const granted = permissions instanceof Set ? permissions : new Set(permissions || []);
  if (item.permission && !granted.has(item.permission)) return false;
  if (item.permissionsAny?.length && !item.permissionsAny.some((permission) => granted.has(permission))) return false;
  if (item.permissionsAll?.length && !item.permissionsAll.every((permission) => granted.has(permission))) return false;
  return true;
}

function buildInheritedSections(...extraSections) {
  const seen = new Set();
  const out = [];
  // 1. Role-specific extras (highest priority, appear on top)
  for (const s of extraSections.filter(Boolean)) {
    out.push(s);
    for (const it of s.items || []) seen.add(it.to);
  }
  // 2. USER base priority (Acompañamiento humano)
  const priorityItems = USER_PRIORITY_SECTION.items.filter(
    (it) => !seen.has(it.to),
  );
  if (priorityItems.length) {
    out.push({ ...USER_PRIORITY_SECTION, items: priorityItems });
    for (const it of priorityItems) seen.add(it.to);
  }
  // 3. USER process (Mi proceso)
  const processItems = USER_PROCESS_SECTION.items.filter(
    (it) => !seen.has(it.to),
  );
  if (processItems.length) {
    out.push({ ...USER_PROCESS_SECTION, items: processItems });
    for (const it of processItems) seen.add(it.to);
  }
  // 4. USER account section always last
  const acctItems = USER_ACCOUNT_SECTION.items.filter((it) => !seen.has(it.to));
  if (acctItems.length) out.push({ ...USER_ACCOUNT_SECTION, items: acctItems });
  return out;
}

export const ROLE_MENUS = {
  user: {
    primary: "topbar.role.user",
    sections: USER_BASE,
  },
  estudiante: {
    primary: "topbar.role.estudiante",
    sections: buildInheritedSections(STUDENT_EXTRAS),
  },
  consultor: {
    primary: "topbar.role.consultor",
    // Consultor conserva la capacidad personal de compra en Marketplace,
    // sin heredar por menú capacidades humanas no concedidas por Dirección.
    sections: [CONSULTOR_EXTRAS, STUDENT_EXTRAS, MARKETPLACE_BUYER_SECTION, ENERGY_PURCHASE_SECTION, USER_ACCOUNT_SECTION],
  },
  creador: {
    primary: "topbar.role.creador",
    // Creador mantiene su identidad y comercio; las herramientas de cliente
    // son permisos explícitos, sin heredar facultades profesionales.
    sections: [CREADOR_EXTRAS, CREATOR_PERSONAL_SERVICES, USER_PRIORITY_SECTION, STUDENT_EXTRAS, MARKETPLACE_BUYER_SECTION, ENERGY_PURCHASE_SECTION, USER_ACCOUNT_SECTION],
  },
  soporte: {
    primary: "topbar.role.soporte",
    sections: [
      SUPPORT_OVERVIEW,
      SUPPORT_CASES,
      SUPPORT_CROSS_PLATFORM,
      ENERGY_PURCHASE_SECTION,
      SUPPORT_ACCOUNT,
    ],
  },
  admin: {
    primary: "topbar.role.admin",
    // El Administrador opera KD9; no hereda compras, academia ni procesos personales.
    sections: [ADMIN_OVERVIEW, ADMIN_OPERATION, ADMIN_CONTROL, ENERGY_PURCHASE_SECTION, ADMIN_ACCOUNT],
  },
  superadmin: {
    primary: "topbar.role.superadmin",
    // El Director no hereda la navegación operativa/personal del usuario.
    // Su panel se organiza por decisiones, gobierno y control.
    sections: [
      DIRECTOR_OVERVIEW,
      DIRECTOR_DECISIONS,
      DIRECTOR_ORACLE,
      DIRECTOR_DIAGNOSTICS,
      DIRECTOR_ACADEMY,
      DIRECTOR_MARKETPLACE,
      DIRECTOR_LANDING,
      DIRECTOR_GOVERNANCE,
      DIRECTOR_CONTROL,
      DIRECTOR_ACCOUNT,
    ],
  },
};


/**
 * Surface cross-role capabilities granted by Dirección without changing the
 * account's primary role or copying another role's whole navigation.
 */
/** Estudio sobre la misma identidad Usuario, sin asignar otro rol. */
export function getAcademicAccessSection(role, accessMap) {
  if (role !== "user" || !accessMap?.flags?.student_active) return null;
  return STUDENT_EXTRAS;
}

export function getDelegatedPermissionSection(role, permissions) {
  const granted = permissions instanceof Set ? permissions : new Set(permissions || []);
  const ownPaths = new Set(
    (ROLE_MENUS[role]?.sections || []).flatMap((section) =>
      (section.items || []).map((item) => item.to),
    ),
  );
  const seen = new Set(ownPaths);
  const items = [];

  Object.entries(ROLE_MENUS).forEach(([candidateRole, menu]) => {
    if (candidateRole === role) return;
    (menu.sections || []).forEach((section) => {
      (section.items || []).forEach((item) => {
        const isPermissionBound = Boolean(
          item.permission || item.permissionsAny?.length || item.permissionsAll?.length,
        );
        if (!isPermissionBound || seen.has(item.to) || !isMenuItemAllowed(item, granted)) return;
        seen.add(item.to);
        items.push(item);
      });
    });
  });

  return items.length
    ? { label: "Accesos autorizados por Dirección", priority: true, items }
    : null;
}

